import type { PublicAudioSource, StreamProvider } from "@hitster/shared";
import type { AudioProvider } from "./AudioProvider";

declare global {
  interface Window {
    onYouTubeIframeAPIReady?: () => void;
    YT?: {
      Player: new (
        elementId: string,
        options: {
          height?: string | number;
          width?: string | number;
          playerVars?: Record<string, number | string>;
          events?: {
            onReady?: () => void;
            onStateChange?: (event: { data: number }) => void;
            onError?: () => void;
          };
        },
      ) => YtPlayer;
      PlayerState: {
        PLAYING: number;
        PAUSED: number;
        ENDED: number;
        UNSTARTED: number;
        CUED: number;
        BUFFERING: number;
      };
    };
    SC?: {
      Widget: new (iframe: HTMLIFrameElement) => ScWidget;
    };
  }
}

interface YtPlayer {
  playVideo(): void;
  pauseVideo(): void;
  stopVideo(): void;
  setVolume(volume: number): void;
  mute(): void;
  unMute(): void;
  cueVideoById(videoId: string): void;
  loadVideoById(videoId: string): void;
  getPlayerState(): number;
}

interface ScWidget {
  bind(event: string, callback: (payload?: { currentPosition?: number }) => void): void;
  play(): void;
  pause(): void;
  setVolume(volume: number): void;
}

let ytApiPromise: Promise<void> | null = null;
let scApiPromise: Promise<void> | null = null;
let lastUserGestureAt = 0;

/** À appeler pendant le clic « Écouter » — requis pour YouTube / SoundCloud. */
export function markStreamPlayGesture(): void {
  lastUserGestureAt = Date.now();
}

export function warmupEmbeddedStream(): void {
  void loadYouTubeApi();
  void loadSoundCloudApi();
}

function loadYouTubeApi(): Promise<void> {
  if (window.YT?.Player) return Promise.resolve();
  if (!ytApiPromise) {
    ytApiPromise = new Promise((resolve) => {
      const previous = window.onYouTubeIframeAPIReady;
      window.onYouTubeIframeAPIReady = () => {
        previous?.();
        resolve();
      };
      if (document.querySelector('script[src="https://www.youtube.com/iframe_api"]')) {
        window.setTimeout(() => resolve(), 3000);
        return;
      }
      const script = document.createElement("script");
      script.src = "https://www.youtube.com/iframe_api";
      script.async = true;
      document.head.appendChild(script);
    });
  }
  return ytApiPromise;
}

function loadSoundCloudApi(): Promise<void> {
  if (window.SC?.Widget) return Promise.resolve();
  if (!scApiPromise) {
    scApiPromise = new Promise((resolve) => {
      if (document.querySelector('script[src="https://w.soundcloud.com/player/api.js"]')) {
        resolve();
        return;
      }
      const script = document.createElement("script");
      script.src = "https://w.soundcloud.com/player/api.js";
      script.async = true;
      script.onload = () => resolve();
      script.onerror = () => resolve();
      document.head.appendChild(script);
    });
  }
  return scApiPromise;
}

function ensureMount(id: string): HTMLElement {
  let mount = document.getElementById(id);
  if (!mount) {
    mount = document.createElement("div");
    mount.id = id;
    mount.className = "hitster-stream-mount";
    document.body.appendChild(mount);
  }
  return mount;
}

function wait(ms: number): Promise<void> {
  return new Promise((resolve) => window.setTimeout(resolve, ms));
}

class EmbeddedStreamAudioProvider implements AudioProvider {
  private provider: StreamProvider | null = null;
  private ytPlayer: YtPlayer | null = null;
  private ytReady = false;
  private scWidget: ScWidget | null = null;
  private scReady = false;
  private scIframe: HTMLIFrameElement | null = null;
  private level = 1;
  private sourceId = "";

  setVolume(volume: number): void {
    this.level = Math.max(0, Math.min(1, volume));
    this.applyVolume();
  }

  private applyVolume(): void {
    const pct = Math.round(this.level * 100);
    if (this.provider === "youtube" && this.ytPlayer && this.ytReady) {
      if (this.level === 0) this.ytPlayer.mute();
      else {
        this.ytPlayer.unMute();
        this.ytPlayer.setVolume(pct);
      }
    }
    if (this.provider === "soundcloud" && this.scWidget && this.scReady) {
      try {
        this.scWidget.setVolume(pct);
      } catch {
        /* widget pas prêt */
      }
    }
  }

  async load(source: PublicAudioSource): Promise<void> {
    if (!source.streamProvider || !source.streamSourceId) {
      throw new Error("Source streaming invalide");
    }
    this.provider = source.streamProvider;
    this.sourceId = source.streamSourceId;

    if (this.provider === "youtube") {
      await loadYouTubeApi();
      await this.ensureYoutubePlayer();
      await this.cueYoutube(this.sourceId);
    } else {
      await loadSoundCloudApi();
      await this.ensureSoundCloud(this.sourceId);
    }
    this.applyVolume();
  }

  private ensureYoutubePlayer(): Promise<void> {
    if (this.ytPlayer && this.ytReady) return Promise.resolve();

    return new Promise((resolve, reject) => {
      const mount = ensureMount("hitster-yt-player");
      if (!mount.querySelector("#hitster-yt-player-inner")) {
        const holder = document.createElement("div");
        holder.id = "hitster-yt-player-inner";
        mount.appendChild(holder);
      }

      if (!window.YT?.Player) {
        reject(new Error("API YouTube indisponible"));
        return;
      }

      let settled = false;
      const finish = (ok: boolean) => {
        if (settled) return;
        settled = true;
        if (ok) resolve();
        else reject(new Error("Lecteur YouTube indisponible"));
      };

      this.ytPlayer = new window.YT.Player("hitster-yt-player-inner", {
        height: 200,
        width: 200,
        playerVars: {
          autoplay: 0,
          rel: 0,
          modestbranding: 1,
          playsinline: 1,
          iv_load_policy: 3,
          enablejsapi: 1,
          origin: window.location.origin,
        },
        events: {
          onReady: () => {
            this.ytReady = true;
            finish(true);
          },
          onError: () => finish(false),
        },
      });

      window.setTimeout(() => {
        if (this.ytReady) finish(true);
      }, 8000);
    });
  }

  private async cueYoutube(videoId: string): Promise<void> {
    if (!this.ytPlayer || !this.ytReady) throw new Error("Lecteur YouTube non prêt");
    this.ytPlayer.cueVideoById(videoId);
    await wait(400);
  }

  private ensureSoundCloud(trackUrl: string): Promise<void> {
    if (this.scWidget && this.scReady && this.sourceId === trackUrl) {
      return Promise.resolve();
    }

    return new Promise((resolve, reject) => {
      const mount = ensureMount("hitster-sc-player");
      mount.innerHTML = "";

      const iframe = document.createElement("iframe");
      iframe.allow = "autoplay";
      iframe.setAttribute("scrolling", "no");
      iframe.setAttribute("frameborder", "no");
      const params = new URLSearchParams({
        url: trackUrl,
        auto_play: "false",
        hide_related: "true",
        show_comments: "false",
        show_user: "false",
        show_reposts: "false",
        show_teaser: "false",
        visual: "false",
        sharing: "false",
      });
      iframe.src = `https://w.soundcloud.com/player/?${params.toString()}`;
      mount.appendChild(iframe);
      this.scIframe = iframe;
      this.scReady = false;
      this.scWidget = null;

      let settled = false;
      const finish = (ok: boolean) => {
        if (settled) return;
        settled = true;
        if (ok) resolve();
        else reject(new Error("Lecteur SoundCloud indisponible"));
      };

      const bindWidget = () => {
        if (!window.SC?.Widget || !this.scIframe) {
          finish(false);
          return;
        }
        this.scWidget = new window.SC.Widget(this.scIframe);
        this.scWidget.bind("ready", () => {
          this.scReady = true;
          finish(true);
        });
        this.scWidget.bind("error", () => finish(false));
      };

      iframe.onload = () => {
        if (window.SC?.Widget) bindWidget();
        else loadSoundCloudApi().then(bindWidget).catch(() => finish(false));
      };

      window.setTimeout(() => {
        if (this.scReady) finish(true);
      }, 8000);
    });
  }

  private async waitForYoutubePlaying(timeoutMs: number): Promise<void> {
    const playing = window.YT?.PlayerState.PLAYING ?? 1;
    const buffering = window.YT?.PlayerState.BUFFERING ?? 3;
    const started = Date.now();
    while (Date.now() - started < timeoutMs) {
      try {
        const state = this.ytPlayer?.getPlayerState();
        if (state === playing || state === buffering) return;
      } catch {
        /* ignore */
      }
      await wait(120);
    }
    throw new Error("Lecture YouTube bloquée");
  }

  async play(): Promise<void> {
    if (this.level === 0) return;

    const gestureAge = Date.now() - lastUserGestureAt;
    if (gestureAge > 12_000) {
      throw new Error("Interaction requise pour lancer l'audio");
    }

    if (this.provider === "youtube" && this.ytPlayer && this.ytReady) {
      this.applyVolume();
      this.ytPlayer.loadVideoById(this.sourceId);
      this.ytPlayer.playVideo();
      try {
        await this.waitForYoutubePlaying(6000);
      } catch {
        await wait(400);
        this.ytPlayer.playVideo();
        await this.waitForYoutubePlaying(4000);
      }
      return;
    }

    if (this.provider === "soundcloud" && this.scWidget && this.scReady) {
      this.applyVolume();
      this.scWidget.play();
      await wait(500);
    }
  }

  pause(): void {
    try {
      this.ytPlayer?.pauseVideo();
    } catch {
      /* ignore */
    }
    try {
      this.scWidget?.pause();
    } catch {
      /* ignore */
    }
  }

  stop(): void {
    this.pause();
    try {
      this.ytPlayer?.stopVideo();
    } catch {
      /* ignore */
    }
  }

  seek(): void {
    /* non utilisé en partie */
  }
}

let embeddedSingleton: EmbeddedStreamAudioProvider | null = null;

export function createEmbeddedStreamProvider(): AudioProvider {
  if (!embeddedSingleton) embeddedSingleton = new EmbeddedStreamAudioProvider();
  return embeddedSingleton;
}

export function isEmbeddedStreamSource(source: PublicAudioSource): boolean {
  return Boolean(source.streamProvider);
}
